'use client';

import { IFormsTemplate } from '@commenda/api';
import { Badge, DataTable, Icons, TableFilter } from '@commenda/kakashi';
import { FormsTemplateStatus } from '@commenda/types';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useFormEditor } from '../hooks/useFormEditor';

interface GroupedTemplate {
  name: string;
  versions: IFormsTemplate[];
  versionCount: number;
  latestVersion: IFormsTemplate;
  hasLiveVersion: boolean;
  key: string | null;
  keyName: string | null;
}

function groupTemplatesByName(templates: IFormsTemplate[]): GroupedTemplate[] {
  const groupMap = new Map<string, IFormsTemplate[]>();

  templates.forEach((template) => {
    const existing = groupMap.get(template.name) || [];
    existing.push(template);
    groupMap.set(template.name, existing);
  });

  const grouped: GroupedTemplate[] = [];
  groupMap.forEach((versions, name) => {
    const sortedVersions = [...versions].sort((a, b) => {
      if (a.status === FormsTemplateStatus.LIVE) return -1;
      if (b.status === FormsTemplateStatus.LIVE) return 1;
      return b.version.localeCompare(a.version, undefined, { numeric: true });
    });
    const hasLiveVersion = versions.some(
      (v) => v.status === FormsTemplateStatus.LIVE
    );
    grouped.push({
      name,
      versions: sortedVersions,
      versionCount: versions.length,
      latestVersion: sortedVersions[0],
      hasLiveVersion,
      key: sortedVersions[0].key ?? null,
      keyName: sortedVersions[0].keyName ?? null,
    });
  });

  return grouped;
}

interface TemplatesTableProps {
  activeFilters: Record<string, string | string[]>;
  onFiltersChange: (filters: Record<string, string | string[]>) => void;
  templates: IFormsTemplate[];
  isLoading: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  limit: number;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  keyOptions: { label: string; value: string | null }[];
  getVersionsHref?: (groupName: string) => string;
  onCreateTemplate?: () => void;
  // A host without a versions screen opens the newest version directly instead.
  onSelectTemplate?: (template: IFormsTemplate) => void;
}

export function TemplatesTable({
  activeFilters,
  onFiltersChange,
  templates,
  isLoading,
  onNextPage,
  onPreviousPage,
  hasPreviousPage,
  hasNextPage,
  limit,
  searchTerm,
  onSearchTermChange,
  keyOptions,
  getVersionsHref,
  onCreateTemplate,
  onSelectTemplate,
}: TemplatesTableProps) {
  const { devMode, editorPath, templatesPath } = useFormEditor();
  const router = useRouter();

  const groupedData = useMemo(
    () => groupTemplatesByName(templates),
    [templates]
  );

  const handleSelectGroup = (groupName: string) => {
    router.push(
      getVersionsHref
        ? getVersionsHref(groupName)
        : `${templatesPath}/versions?name=${encodeURIComponent(groupName)}`
    );
  };

  return (
    <div className="px-4xl">
      <DataTable
        tableContainerClassName="border-0"
        tableWrapperClassName="mb-0"
        isLoading={isLoading}
        columnGroups={[
          {
            id: 'templates',
            columns: [
              {
                id: 'name',
                accessorKey: 'name',
                header: 'Name',
                title: 'Name',
                cell: ({ row }) => {
                  const grouped = row.original as GroupedTemplate;
                  return (
                    <div className="flex flex-col gap-1">
                      <span className="txt-small-medium text-text-primary">
                        {grouped.name}
                      </span>
                    </div>
                  );
                },
              },
              {
                id: 'formKey',
                accessorKey: 'keyName',
                header: 'Form Key',
                title: 'Form Key',
                cell: ({ row }) => {
                  const grouped = row.original as GroupedTemplate;
                  if (!grouped.key) {
                    return (
                      <span className="txt-small-regular text-text-tertiary">
                        Unlinked
                      </span>
                    );
                  }
                  return (
                    <Badge
                      size="sm"
                      variant="gray"
                      iconLeading={<Icons.Key01 className="size-3" />}
                    >
                      {grouped.keyName ?? grouped.key}
                    </Badge>
                  );
                },
              },
              {
                id: 'versionCount',
                accessorKey: 'versionCount',
                header: 'Versions',
                title: 'Versions',
                cell: ({ row }) => {
                  const grouped = row.original as GroupedTemplate;
                  return (
                    <Badge size="sm" variant="gray">
                      {grouped.versionCount}{' '}
                      {grouped.versionCount === 1 ? 'version' : 'versions'}
                    </Badge>
                  );
                },
              },
              {
                id: 'hasLiveVersion',
                accessorKey: 'hasLiveVersion',
                header: 'Has Live Version',
                title: 'Has Live Version',
                cell: ({ row }) => {
                  const grouped = row.original as GroupedTemplate;
                  return grouped.hasLiveVersion ? 'Yes' : 'No';
                },
              },
            ],
          },
        ]}
        emptyStateConfig={{
          title: 'No templates yet',
          description:
            'Create your first form template to get started building forms.',
          action: {
            text: 'Create Template',
            onClick: () => {
              if (onCreateTemplate) {
                onCreateTemplate();
                return;
              }
              router.push(editorPath);
            },
          },
        }}
        tableCellClassName="border-0 h-16"
        tableHeadClassName="border-0"
        data={groupedData}
        onRowClick={(group) => {
          const grouped = group as GroupedTemplate;
          if (onSelectTemplate) {
            onSelectTemplate(grouped.latestVersion);
            return;
          }
          handleSelectGroup(grouped.name);
        }}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search templates...',
          searchValue: searchTerm,
          columnsToEnableSearchFor: ['name'],
          onSearch: (searchValue) => {
            onSearchTermChange(searchValue);
          },
        }}
        filterConfig={{
          filters: [
            {
              columnId: 'status',
              icon: <Icons.FileCheck02 className="size-4" />,
              type: 'MULTI_SELECT',
              label: 'Status',
              options: [
                { label: 'Draft', value: FormsTemplateStatus.DRAFT },
                { label: 'Live', value: FormsTemplateStatus.LIVE },
                { label: 'Completed', value: FormsTemplateStatus.COMPLETED },
              ],
            },
            ...(devMode
              ? ([
                  {
                    columnId: 'key',
                    icon: <Icons.Key01 className="size-4" />,
                    type: 'MULTI_SELECT',
                    label: 'Key',
                    options: [...keyOptions, { label: 'Unlink', value: null }],
                  },
                ] as TableFilter[])
              : []),
          ],
          activeFilters,
          onFilterChange: onFiltersChange,
        }}
        paginationConfig={{
          isEnabled: true,
          pageSize: limit,
          type: 'CURSOR',
          showRowsPerPageSelector: true,
          rowsPerPageOptions: [10, 25, 50, 100],
          onNextPage: () => {
            onNextPage();
          },
          onPreviousPage: () => {
            onPreviousPage();
          },
          hasPreviousPage,
          hasNextPage,
        }}
      />
    </div>
  );
}
